/**
 * <OperationsReportsButton /> — the `Reports →` button every
 * Operations sub-domain hub page surfaces per M3X.REPORTS §0.9.A.
 *
 * Opens the <ReportsLauncherDrawer> for the target domain.
 *
 * Usage:
 *   <OperationsReportsButton
 *     domain="leaves"
 *     domainLabel={t("modules.leaves", "Leaves")}
 *   />
 */

import { ArrowRight, FileText } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { ReportDomain } from "../../lib/reports/catalog";
import { ReportsLauncherDrawer } from "./ReportsLauncherDrawer";

export interface OperationsReportsButtonProps {
  domain: ReportDomain;
  domainLabel: string;
  /** Optional 1-line summary shown in the drawer header. */
  domainHint?: string;
  /** Optional caller-supplied className for layout. */
  className?: string;
}

export function OperationsReportsButton({
  domain,
  domainLabel,
  domainHint,
  className,
}: OperationsReportsButtonProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={[
          "inline-flex items-center gap-1.5 rounded-sm border border-brand-blue/30 bg-brand-blue/5 px-3 py-1.5 text-sm font-medium text-brand-deep hover:bg-brand-blue/10",
          className ?? "",
        ].join(" ")}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <FileText size={14} aria-hidden />
        {t("reports.launcher_button", "Reports")}
        <ArrowRight size={12} aria-hidden className="rtl:rotate-180" />
      </button>
      <ReportsLauncherDrawer
        domain={domain}
        domainLabel={domainLabel}
        domainHint={domainHint}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

export default OperationsReportsButton;
